// Put all packages together.
// Used to generate umd/index.prod.js

import React from 'react';
import { createRoot } from 'react-dom/client';

import { IntlProvider } from 'react-intl';

import TinodeWeb from './views/tinode-web.jsx';
import HashNavigation from './lib/navigation.js';
// bulbal: mặc định tiếng Việt (bỏ browser-detect), vẫn cho override bằng #?hl=xx.
import { BULBAL_DEFAULT_LANG } from './bulbal-config.js';
// bulbal: dải mời cài đặt PWA — root React riêng, KHÔNG thuộc cây <TinodeWeb> (xem chỗ mount
// ở cuối file, sau khi render app chính).
import BulbalInstallPrompt from './widgets/bulbal-install-prompt.jsx';

// Insert google analytics script and tag if configured.
if (typeof FIREBASE_INIT != 'undefined' && FIREBASE_INIT && FIREBASE_INIT.measurementId) {
  const head = document.getElementsByTagName('head')[0];
  let script = document.createElement('script');
  script.src = 'https://www.googletagmanager.com/gtag/js?id=' + FIREBASE_INIT.measurementId;
  script.async = true;
  head.prepend(script);
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('config', FIREBASE_INIT.measurementId);
}

// Allow loading translation strings for just one language.
const messageLoader = {
  'ar': _ => import('./i18n.min/ar.json'),
  'de': _ => import('./i18n.min/de.json'),
  'en': _ => import('./i18n.min/en.json'),
  'es': _ => import('./i18n.min/es.json'),
  'fr': _ => import('./i18n.min/fr.json'),
  'it': _ => import('./i18n.min/it.json'),
  'ko': _ => import('./i18n.min/ko.json'),
  'ro': _ => import('./i18n.min/ro.json'),
  'ru': _ => import('./i18n.min/ru.json'),
  'th': _ => import('./i18n.min/th.json'),
  'uk': _ => import('./i18n.min/uk.json'),
  'vi': _ => import('./i18n.min/vi.json'),
  'zh': _ => import('./i18n.min/zh.json'),
  'zh-TW': _ => import('./i18n.min/zh-TW.json')
};

// List of languages that are written right-to-left.
const rtl = ['ar', 'fa', 'he', 'ur'];

// Detect human language to use in the UI:
//  Check parameters from URL hash #?hl=ru, otherwise use fixed default (bulbal: 'vi' — bỏ
//  browser-detect vì đây là app nội bộ Bulbal, mọi NV nói tiếng Việt). Fallback cuối vẫn 'en'
//  nếu vì lý do gì đó messageLoader không có ngôn ngữ được chọn (xem bên dưới).
const { params } = HashNavigation.parseUrlHash(window.location.hash);
const language = (params && params.hl) || BULBAL_DEFAULT_LANG || 'en';

// Make sure names like 'en_GB' and 'en-GB' consistently use '-'.
const normalized = language.replace('_', '-');
// Get the base language 'en' from a more specific 'en-US' as a partial fallback.
const baseLanguage = normalized.split('-')[0].toLowerCase();

// Try the full locale first, then the locale without the region code, fallback to 'en'.
const htmlLang = messageLoader[normalized] ? language : messageLoader[baseLanguage] ? baseLanguage : 'en';

const html = document.getElementsByTagName('html')[0];
// Set lang attribute of the HTML element: <html lang="XX">
html.setAttribute('lang', htmlLang);
if (rtl.includes(baseLanguage)) {
  // Set right-to-left dir attribute of the HTML element: <html dir="rtl">
  html.setAttribute('dir', 'rtl');
}
// Render the app.
const root = createRoot(document.getElementById('mountPoint'));
messageLoader[htmlLang]().then(messages =>
  root.render(
    React.createElement(IntlProvider,
      { locale: language, messages: messages, textComponent: React.Fragment },
      React.createElement(TinodeWeb, null)
    )
));

// bulbal (R11 — xem plans/20260730-0415-bulbal-chat-mobile-first/phase-04-hoan-thien-pwa.md
// Key Insight #4): đăng ký service worker NGAY Ở ĐÂY, ĐỘC LẬP với Firebase push.
// Trước đợt này, navigator.serviceWorker.register() chỉ tồn tại BÊN TRONG initFCMessaging()
// (src/views/tinode-web.jsx), mà hàm đó chỉ chạy khi isFirebasePushConfigured() = true — hiện
// LUÔN false vì firebase-init.js còn placeholder. Hệ quả đo được trên production: 0 service
// worker nào từng được đăng ký -> Chrome không đủ điều kiện bắn 'beforeinstallprompt' (đòi SW
// có fetch handler) -> lời mời cài PWA (BulbalInstallPrompt) không thể hoạt động, và toàn bộ
// cache-first + chống-cache của service-worker.js cũng không chạy.
// Đăng ký sớm ở đây KHÔNG xung đột với luồng push: initFCMessaging() gọi
// navigator.serviceWorker.getRegistration() TRƯỚC KHI tự register() — nên khi Firebase có
// config thật, nó sẽ tìm thấy và tái dùng đúng service worker đã đăng ký ở đây, không đăng ký
// lần 2 (xem tinode-web.jsx, phương thức initFCMessaging()).
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register('/service-worker.js')
    .catch(err => console.warn('bulbal: đăng ký service worker thất bại', err));
}

// bulbal: mount dải mời cài đặt PWA bằng root React RIÊNG, tách khỏi cây <TinodeWeb>/
// <IntlProvider> ở trên. Lý do tách: (a) ma trận sở hữu file của plan song song tách rời
// tuyệt đối — widget này không được đụng src/views/tinode-web.jsx (đã có phase khác sửa và
// deploy); (b) lời mời cài không cần đọc state của Tinode, không cần react-intl. Gắn thẳng
// vào <body> bằng appendChild (thay vì một phần tử có sẵn trong index.html/index-dev.html)
// -> không phụ thuộc việc sửa 2 file đó cho phần mount này.
const installPromptEl = document.createElement('div');
installPromptEl.id = 'bulbal-install-root';
document.body.appendChild(installPromptEl);
createRoot(installPromptEl).render(React.createElement(BulbalInstallPrompt, null));
